import React from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { Link as RouterLink } from 'react-router-dom'
import { Button, DoorwayFrame, Reveal } from '@/design-system'
import { photo } from './data'
import { APP_NAME } from '@/utils/constants'
import { LogoMark } from '@/features/album/components/EgowallLogo'

const Sec = styled('section')({
  maxWidth: 1240,
  margin: '0 auto',
  padding: 'clamp(70px, 9vw, 120px) clamp(20px, 4vw, 48px) 0',
})

/* ---------- QUOTE ---------- */

const QuoteGrid = styled(Box)({
  paddingTop: 44,
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: 'clamp(20px, 3vw, 44px)',
})

const Rule = styled(Box)({ width: 2.5, background: 'var(--ds-gradient-spectrum)', borderRadius: 3 })

const Quote = styled('blockquote')({
  margin: 0,
  fontFamily: 'var(--ds-font)',
  fontSize: 'clamp(22px, 3vw, 34px)',
  fontWeight: 600,
  letterSpacing: '-0.032em',
  lineHeight: 1.28,
  maxWidth: 820,
  color: 'var(--ds-ink)',
  '& em': {
    fontStyle: 'normal',
    fontWeight: 700,
    background: 'var(--ds-gradient-spectrum)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
})

const Who = styled(Box)({ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 })

const WhoAvatar = styled('span')({
  width: 34,
  height: 34,
  borderRadius: '50%',
  background: 'var(--ds-gradient-spectrum)',
  color: '#fff',
  fontWeight: 800,
  fontSize: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

/* ---------- FINALE ---------- */

const Finale = styled('section')({
  marginTop: 'clamp(80px, 10vw, 130px)',
  position: 'relative',
  background: 'var(--ds-night-canvas)',
  color: 'var(--ds-night-ink)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(ellipse 55% 60% at 50% 108%, rgba(190,157,253,0.2), transparent 65%), radial-gradient(ellipse 40% 45% at 82% 0%, rgba(255,207,125,0.1), transparent 60%)',
  },
})

const FinaleInner = styled(Box)({
  position: 'relative',
  maxWidth: 1240,
  margin: '0 auto',
  padding: 'clamp(80px, 10vw, 130px) clamp(20px, 4vw, 48px)',
  textAlign: 'center',
})

const MonoTop = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.2em',
  color: 'var(--ds-night-ink-subtle)',
  textTransform: 'uppercase',
})

const FinaleH2 = styled('h2')({
  margin: 0,
  marginTop: 22,
  fontFamily: 'var(--ds-font)',
  fontSize: 'clamp(44px, 7vw, 96px)',
  fontWeight: 780,
  letterSpacing: '-0.052em',
  lineHeight: 0.96,
  color: 'var(--ds-night-ink)',
})

const FinaleP = styled('p')({
  margin: '24px auto 0',
  maxWidth: 420,
  fontSize: 15,
  lineHeight: 1.6,
  color: 'var(--ds-night-ink-muted)',
})

const CtaRow = styled(Box)({
  marginTop: 36,
  display: 'flex',
  justifyContent: 'center',
  gap: 12,
  flexWrap: 'wrap',
})

/* ---------- FOOTER ---------- */

const Foot = styled('footer')({
  background: 'var(--ds-night-canvas)',
  color: 'var(--ds-night-ink)',
  borderTop: '1px solid var(--ds-night-border)',
})

const FootInner = styled(Box)({ maxWidth: 1240, margin: '0 auto', padding: '56px clamp(20px, 4vw, 48px) 34px' })

const FootCols = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '2fr 1fr 1fr 1fr',
  gap: 36,
  paddingBottom: 44,
  '@media (max-width: 800px)': { gridTemplateColumns: '1fr 1fr' },
  '@media (max-width: 480px)': { gridTemplateColumns: '1fr', gap: 28 },
})

const ColTitle = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--ds-night-ink-subtle)',
  marginBottom: 13,
})

const FootLink = styled(RouterLink)({
  display: 'block',
  padding: '4px 0',
  fontSize: 13,
  color: 'var(--ds-night-ink-muted)',
  textDecoration: 'none',
  transition: 'color var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': { color: 'var(--ds-night-ink)' },
})

const Wordmark = styled(Box)({
  fontSize: 'clamp(70px, 13vw, 170px)',
  fontWeight: 800,
  letterSpacing: '-0.055em',
  lineHeight: 0.85,
  paddingBottom: '0.3em',
  background: 'var(--ds-gradient-spectrum)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  opacity: 0.92,
  userSelect: 'none',
})

const Legal = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 14,
  paddingTop: 22,
  borderTop: '1px solid var(--ds-night-border)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.12em',
  color: 'var(--ds-night-ink-subtle)',
  flexWrap: 'wrap',
})

/** Quote, finale CTA, and footer — the close of Landing v2. */
export function LandingFinale() {
  const navigate = useNavigate()

  return (
    <>
      <Sec>
        <QuoteGrid>
          <Reveal>
            <Rule />
          </Reveal>
          <Reveal delay={60}>
            <Box>
              <Quote>
                &ldquo;We put a key card on every table. By the last dance,{' '}
                <em>two hundred people were inside the album</em> — and none of them downloaded
                anything.&rdquo;
              </Quote>
              <Who>
                <WhoAvatar>E</WhoAvatar>
                <Box>
                  <Box sx={{ fontWeight: 680 }}>Elena Moretti</Box>
                  <Box sx={{ color: 'var(--ds-ink-subtle)', fontSize: 12 }}>Married June 2026 · Tuscany</Box>
                </Box>
              </Who>
            </Box>
          </Reveal>
        </QuoteGrid>
      </Sec>

      <Finale>
        <FinaleInner>
          <Reveal>
            <MonoTop>THE DOOR IS ONE MINUTE AWAY</MonoTop>
          </Reveal>
          <Reveal delay={60}>
            <FinaleH2>
              Your photos
              <br />
              deserve visitors.
            </FinaleH2>
          </Reveal>
          <Reveal delay={120}>
            <FinaleP>Name it, drop the photos, hand out the key. The room is ready before the song ends.</FinaleP>
          </Reveal>
          <Reveal delay={180}>
            <CtaRow>
              <Button variant='primary' size='lg' onClick={() => navigate('/auth?step=signup')} sx={{ background: '#fff', color: 'var(--ds-ink)' }}>
                Start free →
              </Button>
              <Button variant='secondary' size='lg' onClick={() => navigate('/albums')} sx={{ background: 'rgba(244,243,246,0.1)', color: 'var(--ds-night-ink)' }}>
                See your albums
              </Button>
            </CtaRow>
          </Reveal>
          <Reveal delay={220}>
            <Box sx={{ mt: '60px' }}>
              <DoorwayFrame src={photo('a01-wedding')} caption='ELENA & MARCUS · 12 INSIDE NOW' />
            </Box>
          </Reveal>
        </FinaleInner>
      </Finale>

      <Foot>
        <FootInner>
          <FootCols>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 760, color: 'var(--ds-night-ink)', fontSize: 16 }}>
                <LogoMark />
                {APP_NAME}
              </Box>
              <Box sx={{ mt: '14px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--ds-night-ink-subtle)', maxWidth: 230 }}>
                Photo albums people actually visit. Full resolution, real conversation, one key away.
              </Box>
            </Box>
            <Box>
              <ColTitle>Product</ColTitle>
              <FootLink to='#fidelity'>Fidelity</FootLink>
              <FootLink to='#interactions'>Interactions</FootLink>
              <FootLink to='#key'>The Key</FootLink>
              <FootLink to='#room'>3D Galleries</FootLink>
            </Box>
            <Box>
              <ColTitle>Company</ColTitle>
              <FootLink to='/'>About</FootLink>
              <FootLink to='/'>Journal</FootLink>
              <FootLink to='/'>Careers</FootLink>
            </Box>
            <Box>
              <ColTitle>Support</ColTitle>
              <FootLink to='/'>Help center</FootLink>
              <FootLink to='/privacy'>Privacy</FootLink>
              <FootLink to='/terms'>Terms</FootLink>
            </Box>
          </FootCols>
          <Wordmark>{APP_NAME}</Wordmark>
          <Legal>
            <span>© 2026 EGOWALL, LLC · MEMORIES AT FULL RESOLUTION</span>
          </Legal>
        </FootInner>
      </Foot>
    </>
  )
}
